import type { PolygonWithHoles, Ring, Vec2, Vec3 } from '../../geo/types';
import type { WaterEdge } from '../../site/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { layoutToWorld } from '../../core/coords';
import { orientedFace } from '../fabric/solids';

/** AAR107 fig.2 (1544) and p.8: the south Kattengat continued under the city wall.
 * The 3 m canal, 2.2 m clear arch, submerged levels and unseen vault are estimates.
 * This is a covered drain; the north branch has no connection through the wall. */
export const KATTENGAT_DUIKER = {
  inner: [14, 482] as Vec2,
  intoTown: [9.7, -4.4] as Vec2,
  outerHint: [9.26312, 484.14869] as Vec2,
  width: 3,
  span: 2.2,
  footY: -2.5,
  springY: -1.45,
  /** The vault's buried crown cover stays below the separate, dry ground mesh. */
  topY: -0.01,
  archSegments: 12,
} as const;

export interface CulvertMouth {
  readonly edgeIndex: number;
  readonly centre: Vec2;
  /** Across the mouth, aligned the same way at both ends. */
  readonly across: Vec2;
  readonly halfWidth: number;
}

export interface KattengatDuiker {
  readonly inner: CulvertMouth;
  readonly outer: CulvertMouth;
}

function middle(edge: WaterEdge): Vec2 {
  return [(edge.segment.a[0] + edge.segment.b[0]) / 2,
    (edge.segment.a[1] + edge.segment.b[1]) / 2];
}

function cross(a: Vec2, b: Vec2): number { return a[0] * b[1] - a[1] * b[0]; }
function minus(a: Vec2, b: Vec2): Vec2 { return [a[0] - b[0], a[1] - b[1]]; }

function across(edge: WaterEdge, reference: Vec2): Vec2 {
  const d = minus(edge.segment.b, edge.segment.a);
  const sign = d[0] * reference[0] + d[1] * reference[1] >= 0 ? 1 : -1;
  const length = Math.hypot(...d);
  return [sign * d[0] / length, sign * d[1] / length];
}

/** Resolve mouths on the actual quay edges, so a regenerated shore cannot hide the vault
 * behind a second, solid revetment. Absent unless the reconstructed south fork exists. */
export function kattengatDuikerAt(edges: readonly WaterEdge[]): KattengatDuiker | null {
  const innerIndex = edges.findIndex((edge) => {
    const p = middle(edge);
    return edge.quay === 'wood' && Math.hypot(...minus(p, KATTENGAT_DUIKER.inner)) < 0.25;
  });
  if (innerIndex < 0) return null;
  const innerEdge = edges[innerIndex]!;
  const inner = middle(innerEdge);
  const length = Math.hypot(...KATTENGAT_DUIKER.intoTown);
  const out: Vec2 = [-KATTENGAT_DUIKER.intoTown[0] / length,
    -KATTENGAT_DUIKER.intoTown[1] / length];
  const reference: Vec2 = [out[1], -out[0]];
  const candidates = edges.flatMap((edge, edgeIndex) => {
    if (edge.quay !== 'earth') return [];
    const d = minus(edge.segment.b, edge.segment.a);
    const denominator = cross(out, d);
    if (Math.abs(denominator) < 1e-9) return [];
    const from = minus(edge.segment.a, inner);
    const t = cross(from, d) / denominator;
    const s = cross(from, out) / denominator;
    if (t < 1 || t > 15 || s < 0 || s > 1) return [];
    const centre: Vec2 = [inner[0] + t * out[0], inner[1] + t * out[1]];
    if (Math.hypot(...minus(centre, KATTENGAT_DUIKER.outerHint)) > 3) return [];
    // Both masonry piers fit on this bank segment.
    if (Math.min(s, 1 - s) * Math.hypot(...d) < KATTENGAT_DUIKER.width / 2) return [];
    return [{ edgeIndex, centre, t }];
  }).sort((a, b) => a.t - b.t);
  const outer = candidates[0];
  if (!outer) return null;
  return {
    inner: { edgeIndex: innerIndex, centre: inner, across: across(innerEdge, reference),
      halfWidth: Math.hypot(...minus(innerEdge.segment.a, innerEdge.segment.b)) / 2 },
    outer: { edgeIndex: outer.edgeIndex, centre: outer.centre,
      across: across(edges[outer.edgeIndex]!, reference), halfWidth: KATTENGAT_DUIKER.width / 2 },
  };
}

export function mouthAt(mouth: CulvertMouth, acrossM: number, y: number): Vec3 {
  return layoutToWorld([mouth.centre[0] + acrossM * mouth.across[0],
    mouth.centre[1] + acrossM * mouth.across[1]], y);
}

type Section = readonly [number, number];
const FRAME: Frame = { origin: [0, 0, 0], yaw: 0 };

function arc(): Section[] {
  const r = KATTENGAT_DUIKER.span / 2;
  return Array.from({ length: KATTENGAT_DUIKER.archSegments + 1 }, (_, i): Section => {
    const a = Math.PI * i / KATTENGAT_DUIKER.archSegments;
    return [r * Math.cos(a), KATTENGAT_DUIKER.springY + r * Math.sin(a)];
  });
}

function endTriangles(half: number): Section[][] {
  const points = arc(), n = KATTENGAT_DUIKER.archSegments / 2;
  const left: Section = [-half, KATTENGAT_DUIKER.topY], right: Section = [half, KATTENGAT_DUIKER.topY];
  const leftRim: Section[] = [[-half, KATTENGAT_DUIKER.footY],
    [-KATTENGAT_DUIKER.span / 2, KATTENGAT_DUIKER.footY], ...points.slice(n).reverse()];
  const rightRim: Section[] = [...points.slice(0, n + 1).reverse(),
    [KATTENGAT_DUIKER.span / 2, KATTENGAT_DUIKER.footY], [half, KATTENGAT_DUIKER.footY]];
  const fan = (top: Section, rim: readonly Section[]) =>
    rim.slice(1).map((p, i) => [top, rim[i]!, p]);
  return [...fan(left, leftRim), [left, points[n]!, right], ...fan(right, rightRim)];
}

/** A real vaulted connection below the dry bank, with both ends cut from their quays.
 * The ground above is its top cap; the fortification wall is left intact above ground. */
export function buildKattengatDuiker(
  builder: MeshBuilder,
  duiker: KattengatDuiker,
  wall: VertexStyle,
  lining: VertexStyle
): void {
  const { inner, outer } = duiker;
  const direction = minus(outer.centre, inner.centre);
  const axisLength = Math.hypot(...direction);
  for (const [mouth, sign] of [[inner, -1], [outer, 1]] as const) {
    const normal: Vec3 = [sign * direction[0] / axisLength, 0, -sign * direction[1] / axisLength];
    for (const tri of endTriangles(mouth.halfWidth))
      orientedFace(builder, wall, FRAME, tri.map(([s, y]) => mouthAt(mouth, s, y)), normal);
  }
  const half = KATTENGAT_DUIKER.width / 2, span = KATTENGAT_DUIKER.span / 2;
  const profile: Section[] = [[-half, KATTENGAT_DUIKER.topY], [-half, KATTENGAT_DUIKER.footY],
    [-span, KATTENGAT_DUIKER.footY], ...arc().reverse(),
    [span, KATTENGAT_DUIKER.footY], [half, KATTENGAT_DUIKER.footY], [half, KATTENGAT_DUIKER.topY]];
  profile.forEach((p, i) => {
    const q = profile[(i + 1) % profile.length]!;
    // Open underfoot; the top cap is buried below the existing dry ground above.
    if (p[1] === KATTENGAT_DUIKER.footY && q[1] === KATTENGAT_DUIKER.footY) return;
    const normal: Vec3 = [inner.across[0] * (q[1] - p[1]), p[0] - q[0],
      -inner.across[1] * (q[1] - p[1])];
    const style = Math.max(Math.abs(p[0]), Math.abs(q[0])) <= span + 1e-8 ? lining : wall;
    orientedFace(builder, style, FRAME,
      [mouthAt(inner, p[0], p[1]), mouthAt(outer, p[0], p[1]),
        mouthAt(outer, q[0], q[1]), mouthAt(inner, q[0], q[1])], normal);
  });
}

/** Kept separate from surface-water polygons: the bank remains a continuous dry street. */
export function kattengatCoveredWater(duiker: KattengatDuiker): PolygonWithHoles {
  const span = KATTENGAT_DUIKER.span / 2;
  const at = (mouth: CulvertMouth, s: number): Vec2 =>
    [mouth.centre[0] + s * mouth.across[0], mouth.centre[1] + s * mouth.across[1]];
  const outer: Ring = [at(duiker.inner, -span), at(duiker.inner, span),
    at(duiker.outer, span), at(duiker.outer, -span)];
  return { outer, holes: [] };
}
